import { FC, useEffect, useRef, useState } from 'react';
import { useCreateBeneficiaryMutation, useGetAddRecipientFormConfigQuery } from 'api/beneficiary';
import { BENE_ADDITION_ALERTS, KYB_ERROR_CODES } from 'constants/beneficiary';
import { FormFieldType } from 'constants/kyb';
import { Button, BUTTON_SIZE_TYPES, BUTTON_STATE_TYPES, BUTTON_TYPES } from 'destiny';
import CustomDropdownWidget from 'modules/beneficiary/addRecipient/components/CustomDropdownWidget';
import SkeletonLoader from 'modules/beneficiary/addRecipient/SkeletonLoader';
import { Map } from 'types/kyb';
import { trackMixpanel } from 'utils/mixpanel';
import { toast } from 'utils/toast';
import JsonSchemaForm, { defaultWidgetMap } from 'components/form/jsonSchemaForm/JsonSchemaForm';
import { ButtonLoader } from 'components/loader/ButtonLoader';
import SideDrawer from 'components/SideDrawer';
import { SkeletonTypes } from 'components/skeletons/types';
import CommonWrapper from 'components/wrappers/CommonWrapper';

type AddRecipientProps = {
  isOpen: boolean;
  onClose: (state: boolean) => void;
};

const PLACEHOLDER_VALUE = 'placeholderValue';

const AddRecipient: FC<AddRecipientProps> = ({ isOpen, onClose }) => {
  const { data, isLoading } = useGetAddRecipientFormConfigQuery();
  const [createBeneficiary, { isLoading: isCreating }] = useCreateBeneficiaryMutation();

  const [schema, setSchema] = useState<any>(null);
  const [defaultFormData, setDefaultFormData] = useState<any>(null);
  const [updatedFormData, setUpdatedFormData] = useState<any>(null);
  const [schemaChanged, setSchemaChanged] = useState<boolean>(false);
  const [defaultFormDataChanged, setDefaultFormDataChanged] = useState<boolean>(false);
  const formRef = useRef<any>();

  const [decisionKeys, setDecisionKeys] = useState({ currencyType: '', destinationCurrency: '' });

  const getSchema = (key1?: string, key2?: string) => {
    const default1: string = key1 ?? data?.default;
    const default2: string = key2 ?? data?.[default1?.toLowerCase()]?.default;

    setDecisionKeys({
      currencyType: default1,
      destinationCurrency: default2,
    });

    const defaultLevelData = data?.[default1?.toLowerCase()];

    return defaultLevelData?.currencies?.[default2] ?? defaultLevelData;
  };

  const getDefaultFormData = (properties: any) => {
    return Object.keys(properties).reduce((acc: Map, key: string) => {
      const property = properties[key];
      const displayType = schema?.displayTypes?.[key];
      const placeholderValue = displayType === FormFieldType.INFO ? PLACEHOLDER_VALUE : null;

      const selectValueIndex = property?.enum?.findIndex((item: any) => item?.id === property?.default);
      const selectValue = property?.enum?.[selectValueIndex]?.id;

      acc[key] =
        displayType === FormFieldType.SELECT ||
        displayType === FormFieldType.TABS ||
        displayType === FormFieldType.RADIO
          ? selectValue
          : property?.default ?? placeholderValue;

      return acc;
    }, {});
  };

  useEffect(() => {
    if (!schema && data) handleSchemaChange(getSchema());
  }, [data]);

  useEffect(() => {
    if (schemaChanged && schema?.properties) handleFormDataChange(getDefaultFormData(schema?.properties));
  }, [schemaChanged]);

  const handleSchemaChange = (newSchema: any) => {
    setSchema(newSchema);
    setSchemaChanged(true);
  };

  const handleFormDataChange = (newFormData: any) => {
    setUpdatedFormData(newFormData);
    setDefaultFormData(newFormData);
    setDefaultFormDataChanged(true);
  };

  const handleChange = ({ formData = {} }: { formData: Map }, rootKey: string) => {
    if (rootKey && !schemaChanged) {
      setUpdatedFormData(formData);

      const changedKey = rootKey?.split('_')?.[1];

      if (formData?.[changedKey] !== defaultFormData?.[changedKey])
        if (changedKey === 'currencyType') handleSchemaChange(getSchema(formData[changedKey]));
        else if (changedKey === 'destinationCurrency')
          handleSchemaChange(getSchema(decisionKeys.currencyType?.toLowerCase(), formData[changedKey]));
    }
  };

  const onChangeOverride = (updatedField: any = {}) =>
    setUpdatedFormData((prevUpdatedFormData: any) => ({ ...prevUpdatedFormData, ...updatedField }));

  const handleSubmit = async (formData: Map = {}) => {
    const payload: any = {};

    Object.keys(formData)?.forEach((keyName) => {
      const mappedKey = schema?.keys?.[keyName];
      const dataValue = formData[keyName];

      if (mappedKey && dataValue && dataValue !== PLACEHOLDER_VALUE) payload[mappedKey] = formData[keyName];
    });

    const res: any = await createBeneficiary({
      ...payload,
      payout_method: payload?.currency_type?.toUpperCase() || '',
    });

    let errorMessage = null;
    let successMessage = null;
    let warningMessage = null;

    if (res?.error?.data?.error) {
      const { code, message } = res.error.data.error;

      if (code === 'PARTNER_DOWN') {
        warningMessage =
          'The addition of INR beneficiaries is temporarily unavailable due to an ongoing technical issue with our banking partner. Your request has been received and will be processed shortly.';
      } else {
        errorMessage =
          KYB_ERROR_CODES.indexOf(code) >= 0 ? BENE_ADDITION_ALERTS.KYB_ERROR : message ?? BENE_ADDITION_ALERTS.ERROR;
      }
    } else if (res?.error) {
      errorMessage = BENE_ADDITION_ALERTS.ERROR;
    } else {
      successMessage = BENE_ADDITION_ALERTS.SUCCESS;
    }

    if (successMessage) {
      toast.success(successMessage);
      setTimeout(() => handleClose(), 1000);
    } else if (errorMessage) {
      toast.error(errorMessage);
    } else if (warningMessage) {
      toast.warn(warningMessage);
      setTimeout(() => handleClose(), 1000);
    }
  };

  const onSubmit = ({ formData = {} }: { formData: Map }) => handleSubmit(formData);

  const onError = (errors: any) => {
    if (errors?.length === 0) formRef?.current?.submit?.();
  };

  const handleSubmitButtonclick = () => {
    if (formRef?.current?.validateForm?.()) formRef?.current?.submit?.();
  };

  const handleClose = () => {
    onClose(false);
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={handleClose}
      bodyClassName='tw-w-sideDrawer tw-min-w-sideDrawer tw-h-screen tw-bg-white tw-pt-[34px] tw-pb-[83px] tw-px-6 tw-overflow-y-auto tw-no-scrollbar'
      closeBtnStyle='!tw-top-[29px] !tw-right-[30px]'
    >
      <CommonWrapper
        isLoading={isLoading || !schema || !defaultFormData}
        skeletonType={SkeletonTypes.CUSTOM}
        skeleton={<SkeletonLoader />}
      >
        <div className='tw-w-full'>
          <JsonSchemaForm
            schema={schema}
            formData={schemaChanged ? defaultFormData : updatedFormData}
            resetUiSchema={schemaChanged && defaultFormDataChanged}
            onUiSchemaSet={() => setSchemaChanged(false)}
            onChange={handleChange}
            onSubmit={onSubmit}
            onError={onError}
            customProps={{ onChangeOverride, schemaId: 'ADD_RECIPIENT' }}
            displayTypes={schema?.displayTypes}
            liveValidate={false}
            ref={formRef}
            skeleton={<SkeletonLoader />}
            widgetMap={{ ...defaultWidgetMap, [FormFieldType.SELECT]: CustomDropdownWidget }}
            buttons={
              <Button
                buttonProps={{
                  type: 'submit',
                  btnType: BUTTON_TYPES.PRIMARY,
                  size: BUTTON_SIZE_TYPES.LARGE,
                  state: BUTTON_STATE_TYPES.DEFAULT,
                  isLoading: isCreating,
                  wrapperClass: 'tw-w-full tw-mt-2.5',
                  onClick: handleSubmitButtonclick,
                  id: 'ADD_RECIPIENT_FORM_BUTTON',
                  eventCallback: trackMixpanel,
                }}
                loader={<ButtonLoader />}
              >
                Add Recipient
              </Button>
            }
            id='ADD_RECIPIENT_FORM'
          />
        </div>
      </CommonWrapper>
    </SideDrawer>
  );
};

export default AddRecipient;
